import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { KIND_LABELS } from '@cerebro-diem/core';
import type { ItemKind } from '@cerebro-diem/core';
import { errorMessage } from '../services/errors';
import { deleteItem, sourceCaptures } from '../services/items';
import { queryKeys, refreshFilingViews } from '../services/queries';
import { dangerButton, outlineButton } from './styles';

/**
 * Delete a task or note, after a confirm step. When it was filed from a
 * capture, the confirm offers to delete that capture too, on by default: the
 * capture is the same thing, so an item moved out of Cerebro leaves nothing
 * behind. A capture that is kept goes back to the Inbox for review.
 * `onDeleted` runs once it is gone (a page of its own leaves).
 *
 * It sits in a form's Save row: the button keeps to the right of the row,
 * and the confirm takes a full row of its own below it.
 */
export default function DeleteItem({
  kind,
  id,
  name,
  onDeleted,
}: {
  kind: ItemKind;
  id: string;
  name: string;
  onDeleted?: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const label = KIND_LABELS[kind].toLowerCase();
  if (!confirming) {
    return (
      <button type="button" onClick={() => setConfirming(true)} className={`${outlineButton} ml-auto inline-flex items-center gap-1 text-error`}>
        <Trash2 className="w-4 h-4" aria-hidden="true" />
        Delete {label}…
      </button>
    );
  }
  return <DeleteConfirm kind={kind} id={id} name={name} label={label} onDeleted={onDeleted} onCancel={() => setConfirming(false)} />;
}

function DeleteConfirm({
  kind,
  id,
  name,
  label,
  onDeleted,
  onCancel,
}: {
  kind: ItemKind;
  id: string;
  name: string;
  label: string;
  onDeleted?: () => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [alsoCapture, setAlsoCapture] = useState(true);
  const sources = useQuery({ queryKey: queryKeys.sourceCaptures(kind, id), queryFn: () => sourceCaptures(kind, id) });
  const hasSource = (sources.data?.length ?? 0) > 0;
  const mutation = useMutation({
    mutationFn: () => deleteItem(kind, id, hasSource && alsoCapture),
    onSuccess: () => {
      onDeleted?.();
      // Its own query (and the note's people) is gone and must not be fetched again.
      queryClient.removeQueries({ queryKey: kind === 'task' ? queryKeys.task(id) : queryKeys.note(id) });
      // Its lists, its container's counts, and the Inbox a kept capture goes back to.
      return refreshFilingViews(queryClient);
    },
  });

  if (mutation.isSuccess) return <p className="basis-full text-sm text-on-surface-variant">Deleted.</p>;

  return (
    <div role="group" aria-label={`Delete this ${label}`} className="basis-full rounded-xl border border-error/30 bg-error/5 p-3 space-y-2">
      <p className="text-sm font-medium break-words">
        Delete the {label} “{name}”? This cannot be undone.
      </p>
      {sources.isError ? (
        <p className="text-sm text-error">Could not check for the capture it came from: {errorMessage(sources.error)}</p>
      ) : sources.isPending ? (
        <p className="text-sm text-on-surface-variant">Checking for the capture it came from…</p>
      ) : (
        hasSource && (
          <>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={alsoCapture}
                onChange={(e) => setAlsoCapture(e.target.checked)}
                disabled={mutation.isPending}
                className="mt-1"
              />
              <span className="min-w-0">
                {sources.data.length === 1 ? 'Also delete the capture it came from' : `Also delete the ${sources.data.length} captures it came from`}
                {sources.data.map((capture) => (
                  <span key={capture.id} className="block text-xs text-on-surface-variant whitespace-pre-wrap break-words line-clamp-3">
                    “{capture.rawText}”
                  </span>
                ))}
              </span>
            </label>
            {!alsoCapture && <p className="text-xs text-on-surface-variant">A capture you keep goes back to the Inbox for review.</p>}
          </>
        )
      )}
      {mutation.isError && <p className="text-sm text-error">Not deleted: {errorMessage(mutation.error)}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending || !sources.isSuccess} className={dangerButton}>
          {mutation.isPending ? 'Deleting…' : `Delete ${label}`}
        </button>
        <button type="button" onClick={onCancel} disabled={mutation.isPending} className={outlineButton}>
          Keep it
        </button>
      </div>
    </div>
  );
}
